import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { NumberInput } from '@/components/ui/number-input';
import { ArrowRight, ListOrdered, Loader2 } from 'lucide-react';
import {
  previewSeriesEpisodeRenumber,
  renumberSeriesEpisodes,
  type EpisodeRenumberChange,
} from '@littlebooker/dal';

interface RenumberEpisodesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  seriesId: string;
  /** Series or show name, for the dialog copy. */
  seriesName?: string;
  /**
   * Pivot: resequence from this instance onward. Episodes before it keep their numbers.
   * Omit to resequence the whole series.
   */
  fromDate?: string;
  /** Called after a successful apply, with the changes that were written. */
  onApplied?: (changes: EpisodeRenumberChange[]) => void;
}

const formatDate = (change: EpisodeRenumberChange) => {
  const iso = change.series_instance_date || change.start_time;
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export function RenumberEpisodesDialog({
  open,
  onOpenChange,
  seriesId,
  seriesName,
  fromDate,
  onApplied,
}: RenumberEpisodesDialogProps) {
  const queryClient = useQueryClient();
  // Empty = "continue from the episode before the pivot", which is what you want almost always.
  const [startAt, setStartAt] = useState<number | null>(null);
  const [scopeWholeSeries, setScopeWholeSeries] = useState(false);

  const effectiveFromDate = scopeWholeSeries ? undefined : fromDate;

  const previewQuery = useQuery({
    queryKey: ['episodeRenumberPreview', seriesId, effectiveFromDate ?? 'all', startAt],
    queryFn: () =>
      previewSeriesEpisodeRenumber({
        seriesId,
        fromDate: effectiveFromDate,
        startAt: startAt ?? undefined,
      }),
    enabled: open,
  });

  const applyMutation = useMutation({
    mutationFn: () =>
      renumberSeriesEpisodes({
        seriesId,
        fromDate: effectiveFromDate,
        startAt: startAt ?? undefined,
      }),
    onSuccess: (changes) => {
      queryClient.invalidateQueries({ queryKey: ['happenings'] });
      queryClient.invalidateQueries({ queryKey: ['episodeRenumberPreview'] });
      onApplied?.(changes);
      onOpenChange(false);
    },
  });

  const changes = previewQuery.data ?? [];
  const slugChanges = changes.filter((c) => c.new_slug);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="large">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListOrdered className="h-5 w-5" />
            Renumber episodes{seriesName ? ` — ${seriesName}` : ''}
          </DialogTitle>
          <DialogDescription>
            Episodes are numbered once, when they are generated, so deleting one from the middle of
            a series leaves every later episode one number too high. This resequences them
            gap-free. Nothing is written until you apply.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 px-4 sm:px-6 py-4">
          <div className="flex flex-wrap items-end gap-4">
            {fromDate && (
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={scopeWholeSeries}
                  onChange={(e) => setScopeWholeSeries(e.target.checked)}
                  disabled={applyMutation.isPending}
                />
                Renumber the whole series, not just this episode onward
              </label>
            )}
            <div className="space-y-1">
              <label className="text-sm font-medium">Start the sequence at</label>
              <NumberInput
                min={1}
                allowEmpty
                value={startAt}
                onChange={(n) => setStartAt(n)}
                placeholder="Auto"
                className="w-32 rounded-md border border-input bg-background px-3 py-2 text-sm"
                disabled={applyMutation.isPending}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to continue from the episode before.
              </p>
            </div>
          </div>

          {previewQuery.isLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Working out the new numbering…
            </div>
          ) : previewQuery.isError ? (
            <p className="py-6 text-sm text-destructive">
              Could not build a preview: {(previewQuery.error as Error).message}
            </p>
          ) : changes.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">
              Nothing to change — the episode numbers are already sequential.
            </p>
          ) : (
            <>
              <div className="max-h-80 overflow-y-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted">
                    <tr className="text-left">
                      <th className="px-3 py-2 font-medium">Date</th>
                      <th className="px-3 py-2 font-medium">Show</th>
                      <th className="px-3 py-2 font-medium">Episode</th>
                      <th className="px-3 py-2 font-medium">URL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changes.map((change) => (
                      <tr key={change.happening_id} className="border-t">
                        <td className="whitespace-nowrap px-3 py-2">{formatDate(change)}</td>
                        <td className="px-3 py-2">
                          <span className="line-clamp-1">{change.title}</span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2">
                          <span className="text-muted-foreground">
                            {change.current_episode_number ?? '—'}
                          </span>
                          <ArrowRight className="mx-1 inline h-3 w-3 text-muted-foreground" />
                          <span className="font-semibold">{change.new_episode_number}</span>
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {change.new_slug ? (
                            <span className="line-clamp-1">updated to {change.new_slug}</span>
                          ) : (
                            'unchanged'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">
                {changes.length} episode{changes.length !== 1 ? 's' : ''} will be renumbered
                {slugChanges.length > 0
                  ? `, ${slugChanges.length} of which also get a new URL (the old one keeps redirecting).`
                  : '. No URLs change — published and past episodes keep their existing links.'}
              </p>
            </>
          )}

          {applyMutation.isError && (
            <p className="text-sm text-destructive">
              Renumbering failed: {(applyMutation.error as Error).message}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={applyMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => applyMutation.mutate()}
            disabled={changes.length === 0 || previewQuery.isLoading || applyMutation.isPending}
          >
            {applyMutation.isPending
              ? 'Renumbering…'
              : `Renumber ${changes.length} episode${changes.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
